import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { StatusDot } from '@/features/accounts/StatusDot';
import { formatAccountLabel, type Account } from '@/features/accounts/accounts.api';
import { useUnarchiveAccount } from '@/features/accounts/useArchiveAccount';

interface ArchivedAccountsSectionProps {
  /** The hidden accounts (`archivedAccounts(...)`), in list order. */
  accounts: Account[];
}

/**
 * Admin-only collapsed section at the bottom of the account pool: 「已隐藏 (N)」
 * — expands to the hidden rows, each with a 「恢复」 action
 * (`POST /accounts/{id}/unarchive`, DB-only). Renders nothing when N = 0; the
 * caller renders it only for admins.
 *
 * Collapsed by default and deliberately plain (no unread badge, no select):
 * a hidden row is out of the pool — its old conversations stay reachable
 * from 「所有账号」 and search, labelled as before.
 */
export function ArchivedAccountsSection({
  accounts,
}: ArchivedAccountsSectionProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const unarchive = useUnarchiveAccount();

  if (accounts.length === 0) return null;

  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <li className="pt-1">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Chevron className="size-3.5 shrink-0" aria-hidden="true" />
        已隐藏 ({accounts.length})
      </button>
      {expanded && (
        <ul className="mt-1 flex flex-col gap-1" data-testid="archived-accounts-list">
          {accounts.map((account) => {
            const label = formatAccountLabel(account);
            return (
              <li
                key={account.id}
                className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] text-muted-foreground"
              >
                <StatusDot
                  status={account.status}
                  statusSince={account.status_since}
                  statusDetail={account.status_detail}
                />
                <span className="min-w-0 flex-1 truncate">{label}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={unarchive.isPending}
                  onClick={() => unarchive.mutate(account.id)}
                  aria-label={`恢复 ${label}`}
                  className="h-6 shrink-0 px-2 text-[11px] font-bold"
                >
                  恢复
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
